import { DateTimeParseException, LocalDate } from '@js-joda/core';
import { DateSerializable } from '../DateSerializable';
import { Effect, Option, Schema, SchemaGetter, SchemaIssue } from 'effect';

import { LocalDateClass } from '../primitive/localDate';

/**
 * Schema that transforms between SerializableDate and LocalDate.
 * Useful for Schema-based workflows when you need to work with LocalDate instances.
 *
 * Decode: `{ _tag: 'Date', value: '2024-12-10' }` → `LocalDate` instance
 * Encode: `LocalDate` instance → `{ _tag: 'Date', value: '2024-12-10' }`
 *
 * Use this when you have a SerializableDate from an API response and need
 * to perform date calculations using JS-Joda LocalDate methods.
 */
export const LocalDateFromDateSerializable = DateSerializable.pipe(
  Schema.decodeTo(LocalDateClass, {
    decode: SchemaGetter.transformEffect((input) =>
      Effect.try({
        try: () => LocalDate.parse(input.value),
        catch: (e) =>
          new SchemaIssue.InvalidValue({
            message:
              e instanceof DateTimeParseException
                ? e.message
                : `Invalid date value: ${input.value}`,
          }),
      })
    ),
    encode: SchemaGetter.transform((localDate) =>
      DateSerializable.make({
        _tag: 'Date',
        value: localDate.toString(),
      })
    ),
  })
);

export type LocalDateFromDateSerializable = Schema.Schema.Type<
  typeof LocalDateFromDateSerializable
>;

export const decodeLocalDateFromDateSerializable = Schema.decodeEffect(
  LocalDateFromDateSerializable
);
